import { NgClass } from '@angular/common';
import { Component, DestroyRef, OnInit, ViewChild, ViewContainerRef } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { ReactiveFormsModule, UntypedFormControl, UntypedFormGroup, Validators } from '@angular/forms';
import { DomSanitizer, SafeHtml } from '@angular/platform-browser';
import { ActivatedRoute, Router } from '@angular/router';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { otpHandler } from 'authentication/functions/otp-handler';
import { AuthenticationService } from 'authentication/services/authentication.service';
import { EMPTY, Observable, catchError, debounceTime, filter, fromEvent, of, switchMap, tap } from 'rxjs';
import { Supplier } from 'settings/interfaces/supplier.interface';
import { SuppliersService } from 'settings/services/suppliers.service';
import { MenuComponent, PasswordInputComponent } from 'shared/components';
import { FormErrorComponent } from 'shared/components/form-error/form-error.component';
import { IMAGES } from 'shared/constants/imagePaths.constants';
import { Language, supportedLanguages } from 'shared/constants/languages';
import { TrimInputDirective } from 'shared/directives/trim-input.directive';
import { UserCredentials } from 'shared/interfaces/user-credentials.interface';
import { UserInviteInfo } from 'shared/interfaces/user-invite.interface';
import { ModalService, ToastService } from 'shared/services';
import { EventBusService } from 'shared/services/event-bus.service';
import { FileService } from 'shared/services/file.service';
import { UserApiService } from 'shared/services/userApi.service';
import { mustMatch } from 'shared/validators/must-match-validator';
import { passwordPattern } from 'shared/validators/password-pattern-validator';

@Component({
    selector: 'app-user-onboarding',
    templateUrl: './user-onboarding.component.html',
    styleUrls: ['./user-onboarding.component.scss'],
    imports: [TranslateModule, NgClass, MenuComponent, FormErrorComponent, ReactiveFormsModule, TrimInputDirective, PasswordInputComponent],
})
export class UserOnboardingComponent implements OnInit {
    @ViewChild('languageAppMenu') private languageAppMenu!: MenuComponent;
    @ViewChild('termsAndConditionsDocumentElement', { read: ViewContainerRef })
    private termsAndConditionsDocumentElement!: ViewContainerRef;

    public userEmail!: string;
    public supplier!: Supplier;
    public currentStep = 0;
    public steps: string[] = [];
    public userInvite!: UserInviteInfo;
    public supportedLanguages = supportedLanguages;
    public currentLanguage: string = '';
    public wasScrollOnBottom: boolean = false;
    public termsAndConditionsDocumentHTML!: SafeHtml;
    public fileContent!: string;
    public registrationForm = new UntypedFormGroup({
        firstName: new UntypedFormControl('', [Validators.required, Validators.maxLength(32)]),
        lastName: new UntypedFormControl('', [Validators.required, Validators.maxLength(32)]),
        password: new UntypedFormControl('', [Validators.required, Validators.minLength(8), passwordPattern()]),
        confirmPassword: new UntypedFormControl('', [Validators.required, mustMatch('password')]),
    });
    public termsAndConditionsForm = new UntypedFormGroup({
        acceptTermsAndConditions: new UntypedFormControl({ value: false, disabled: true }, Validators.requiredTrue),
    });

    protected IMAGES = IMAGES;

    private inviteId?: string;

    constructor(
        private activatedRoute: ActivatedRoute,
        private translate: TranslateService,
        private toastService: ToastService,
        private userApiService: UserApiService,
        private supplierService: SuppliersService,
        private router: Router,
        private destroyRef: DestroyRef,
        private authenticationService: AuthenticationService,
        private domSanitizer: DomSanitizer,
        private eventBus: EventBusService,
        private fileService: FileService,
        private modalService: ModalService,
    ) {}

    public ngOnInit(): void {
        this.currentLanguage = this.translate.getCurrentLang() ?? supportedLanguages[0].languageCode;

        this.translate
            .stream(['users.onboarding.stepper.general', 'users.onboarding.stepper.termsAndConditions'])
            .pipe(takeUntilDestroyed(this.destroyRef))
            .subscribe((translation: { [key: string]: string }) => {
                this.steps = [...Object.values(translation)];
            });

        this.activatedRoute.params
            .pipe(
                takeUntilDestroyed(this.destroyRef),
                filter((params) => Boolean(params.inviteId)),
                switchMap((params) => {
                    this.inviteId = params.inviteId as string;
                    return (this.userApiService.getUserInviteData(this.inviteId) as Observable<UserInviteInfo>).pipe(
                        catchError((error) => {
                            if ([404, 412].includes(error.status)) {
                                return of(error.error) as Observable<UserInviteInfo>;
                            }

                            return EMPTY;
                        }),
                    );
                }),
                tap((userInviteInfo: UserInviteInfo) => {
                    this.userInvite = userInviteInfo;
                }),
                filter((userInvite) => Boolean(userInvite.email)),
                switchMap((userInviteInfo: UserInviteInfo) => {
                    this.userEmail = userInviteInfo.email;
                    if (userInviteInfo.identityCreatedExternal) {
                        this.registrationForm.removeControl('password');
                        this.registrationForm.removeControl('confirmPassword');
                    }

                    //TODO: Shiji Users have other terms and conditions
                    if (userInviteInfo.isShijiInvite || !userInviteInfo.supplierId) {
                        return EMPTY;
                    }

                    return this.userApiService.getInviteContext(this.inviteId!, this.userEmail).pipe(
                        catchError(() => {
                            this.showInviteContextError();
                            return EMPTY;
                        }),
                        switchMap(({ token }) =>
                            this.supplierService.getSupplierPublicData(userInviteInfo.supplierId, token).pipe(
                                catchError(() => {
                                    this.showInviteContextError();
                                    return EMPTY;
                                }),
                            ),
                        ),
                    );
                }),
                filter((supplierData) => Boolean(supplierData)),
                tap((supplierData) => {
                    this.supplier = supplierData;
                }),
                switchMap((supplier) => {
                    return this.fileService.getFile(supplier.termsAndConditions!);
                }),
            )
            .subscribe((tcHtml: string) => {
                this.fileContent = tcHtml;
                this.termsAndConditionsDocumentHTML = this.domSanitizer.bypassSecurityTrustHtml(tcHtml);
            });

        this.registrationForm
            .get('password')
            ?.valueChanges.pipe(takeUntilDestroyed(this.destroyRef))
            .subscribe(() => {
                this.registrationForm.get('confirmPassword')?.updateValueAndValidity();
            });
    }

    public nextStep(): void {
        this.registrationForm.markAllAsTouched();

        if (!this.registrationForm.valid) {
            return;
        }

        this.currentStep++;
        if (this.currentStep === 1) {
            this.watchScrollTerms();
        }
    }

    public previousStep(): void {
        this.currentStep--;
        if (this.currentStep === 1) {
            this.watchScrollTerms();
        }
    }

    public resetWasScrollOnBottom() {
        const element: HTMLElement = this.termsAndConditionsDocumentElement?.element?.nativeElement;

        if (!element) {
            this.wasScrollOnBottom = false;
        } else {
            const height = element.scrollHeight - element.offsetHeight - 1;

            if (element.scrollTop >= height) {
                this.wasScrollOnBottom = true;
            } else {
                // Scroll was already on bottom
                this.wasScrollOnBottom = Boolean(this.termsAndConditionsForm.get('acceptTermsAndConditions')?.enabled);
            }
        }

        if (this.wasScrollOnBottom) {
            this.termsAndConditionsForm.get('acceptTermsAndConditions')?.enable();
        } else {
            this.termsAndConditionsForm.get('acceptTermsAndConditions')?.disable();
        }
    }

    public watchScrollTerms(tryCount: number = 0): void {
        const elm: HTMLElement = this.termsAndConditionsDocumentElement?.element?.nativeElement;
        if (!elm) {
            tryCount = tryCount || 0;
            tryCount++;
            if (tryCount <= 10) {
                // Timeout OK
                setTimeout(() => this.watchScrollTerms(tryCount), 100);
            }
            return;
        }
        this.termsAndConditionsForm.get('acceptTermsAndConditions')?.disable();
        this.resetWasScrollOnBottom();

        // this.subscriptions.scrollTerms?.unsubscribe();
        fromEvent(elm, 'scroll')
            .pipe(takeUntilDestroyed(this.destroyRef), debounceTime(100))
            .subscribe(() => {
                this.resetWasScrollOnBottom();
            });
    }

    public finishRegistration() {
        this.termsAndConditionsForm.markAllAsTouched();

        if (!this.termsAndConditionsForm.valid) {
            return;
        }

        const userCredentials = {
            email: this.userEmail,
            password: this.registrationForm.value.password,
            firstName: this.registrationForm.value.firstName,
            lastName: this.registrationForm.value.lastName,
        } as UserCredentials;

        this.userApiService
            .getInviteContext(this.inviteId!, this.userEmail)
            .pipe(
                takeUntilDestroyed(this.destroyRef),
                catchError(() => {
                    this.showInviteContextError();
                    return EMPTY;
                }),
                switchMap(({ token }) => this.userApiService.acceptInvite(userCredentials, token)),
                catchError((error) => {
                    if (error.status === 409) {
                        this.toastService.showToast({
                            body: 'users.onboarding.conflict',
                            severity: 'error',
                            duration: 10000,
                            ignoreRemoval: true,
                        });

                        return EMPTY;
                    }

                    throw error;
                }),
                switchMap(() => {
                    if (!userCredentials.password) {
                        this.toastService.showToast({
                            body: 'users.onboarding.success',
                            severity: 'success',
                            duration: 10000,
                            ignoreRemoval: true,
                        });
                        return of(null);
                    }

                    return this.authenticationService.login(userCredentials);
                }),
                filter((response) => {
                    return otpHandler(response, userCredentials, this.toastService, this.modalService, this.router, this.destroyRef);
                }),
            )
            .subscribe(() => this.router.navigate(['/']));
    }

    public downloadTCCopy() {
        if (!this.supplier.termsAndConditions || !this.fileContent) {
            return;
        }
        const documentFileName = this.supplier.termsAndConditions.substring(this.supplier.termsAndConditions.lastIndexOf('/') + 1);
        const tempLink = document.createElement('a');
        const taBlob = new Blob([this.fileContent]);

        tempLink.setAttribute('href', URL.createObjectURL(taBlob));
        tempLink.setAttribute('download', documentFileName);
        tempLink.click();
        URL.revokeObjectURL(tempLink.href);
        tempLink.remove();
    }

    public changeLanguage(language: Language) {
        this.eventBus.dispatch('changeLanguage', language.languageCode);
        this.currentLanguage = language.languageCode;
        this.languageAppMenu.closeMenu();
    }

    private showInviteContextError(): void {
        this.toastService.showToast({
            body: 'users.onboarding.unexpectedError',
            severity: 'error',
            duration: 10000,
            ignoreRemoval: true,
        });
    }
}
